import * as React from "react";
import { styled } from "styled-components";
import { cssFontSize, cssSpacing } from "../../util/styles.js";
import { search as searchIcon, circleX as closeIcon } from "../../icons/index.js";

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  name?: string;
  autoFocus?: boolean;
  onClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  label,
  placeholder = "Søk",
  name,
  autoFocus,
  onClear,
  onFocus,
  onBlur,
  className,
  inputRef,
}) => (
  <Wrapper className={className}>
    {label && <InputLabel>{label}</InputLabel>}
    <Field>
      <Icon>{searchIcon}</Icon>
      <input
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        ref={inputRef}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {onClear && value && (
        <Clear type="button" aria-label="Tøm søk" onClick={onClear}>
          {closeIcon}
        </Clear>
      )}
    </Field>
  </Wrapper>
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputLabel = styled.span`
  display: block;
  font-size: ${cssFontSize("tiny")};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary-text-color);
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssSpacing("xs")};
  border: 2px solid var(--searchbar-border-color);
  border-radius: ${(props) => props.theme.borderRadius};
  padding: ${cssSpacing("xs")};
  background: transparent;

  &:focus-within {
    border-color: var(--searchbar-focus-color);
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    font-size: ${cssFontSize("small")};
    font-family: inherit;
    background: transparent;
    color: var(--searchbar-text-color);
    appearance: none;
    padding: 0;

    &::placeholder {
      color: var(--primary-text-color);
    }
  }
`;

const Icon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;

    path,
    circle {
      stroke: var(--searchbar-icon-color);
    }
  }
`;

const Clear = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;

    path {
      stroke: var(--secondary-icon-color);
    }
  }
`;
